// The portfolio deal model: what nventr charges a private-equity firm that
// rolls the platform across its portfolio companies, what it costs nventr
// to serve, and what the firm saves. Pure: inputs → yearly lines. The
// figure in components/portfolio/DealModel.js renders it; the NoVA tour
// changes inputs through the URL (see parseInputs / encodeInputs).
//
// Every price is the published rate card (legal/pricing.md, plans.js) or
// is marked `proposed`. Every cost is marked `actual` (a catalog price we
// pay) or `estimated` (a planning figure) — the cost ledger's rule.

// ---- rate card (what we charge) ------------------------------------------------
export const RATE_CARD = {
  enterpriseBaseMo: 8000,       // Platform Enterprise, per portco org — legal/pricing.md
  enterpriseBuilders: 25,
  enterpriseOperators: 100,
  enterpriseCreditsMo: 400000,
  creditUsd: 0.010,             // Runtime Credit, Enterprise rate
  credits: { run: 2, conversation: 10, commit: 2, hitl: 1 },
  kitMo: 149,
  kitBusinessMo: 349,
  kitEnterpriseFirmMo: 3000,    // one envelope per firm: multi-instance, phone-home, named support
  kitDevRunsIncluded: 8,
  kitBusinessDevRunsIncluded: 40,
  devRunUsd: 1.50,
  hostedTokenUsdPer1k: 0.02,
  hostedTokenAllowanceUsdMo: 150, // included hosted-model allowance per org (billing.credits.includedUsd pro)
  tokensPerConversation: 7500,  // ≈ 6K in + 1.5K out, a ticket-style conversation
  extraBuilderMo: 39,           // proposed — seats past the Enterprise bundle; not on the public card
  extraOperatorMo: 12,          // proposed
};

// ---- cost card (what it costs us) ----------------------------------------------
export const COST_CARD = {
  hostKitMo: { usd: 45, basis: 'estimated', note: 'shared host slot + memory share, cost-ledger apportionment' },
  hostKitBusinessMo: { usd: 120, basis: 'estimated', note: 'larger slot, dedicated workers' },
  creditCompute: { usd: 0.0015, basis: 'estimated', note: 'Fargate seconds + Firestore ops per Runtime Credit' },
  routedTokenPer1k: { usd: 0.0003, basis: 'actual', note: 'deepseek-v4-flash catalog price, blended in/out' },
  frontierTokenPer1k: { usd: 0.0012, basis: 'actual', note: 'Fable 5.1 catalog price at the measured 94.6% cache-read mix' },
  devRun: { usd: 0.65, basis: 'estimated', note: 'cloud agent run + runner compute' },
  planePerPortcoMo: { usd: 150, basis: 'estimated', note: 'control plane share: Firestore, ECS, mail, monitoring' },
  supportPerPortcoMo: { usd: 400, basis: 'estimated', note: 'named support, one CSM per ten portcos' },
  servicesDelivery: { share: 0.55, basis: 'estimated', note: 'people cost on pilot and first-pipeline services' },
};

// Usage maturity by year (share of the steady-state usage inputs).
export const USAGE_RAMP = [0.5, 1, 1.5];

// ---- inputs ---------------------------------------------------------------------
// group · key · label · unit · [min, max, step]
export const INPUT_SPEC = [
  { group: 'Portfolio', key: 'portcos', label: 'Portfolio companies', unit: '', range: [5, 120, 1] },
  { group: 'Portfolio', key: 'adoption1', label: 'Live in year 1', unit: '', range: [1, 120, 1] },
  { group: 'Portfolio', key: 'adoption2', label: 'Live in year 2', unit: '', range: [1, 120, 1] },
  { group: 'Portfolio', key: 'adoption3', label: 'Live in year 3', unit: '', range: [1, 120, 1] },
  { group: 'Portfolio', key: 'builders', label: 'Builders per company', unit: 'seats', range: [1, 200, 1] },
  { group: 'Portfolio', key: 'operators', label: 'Operators per company', unit: 'seats', range: [1, 1000, 5] },
  { group: 'Apps', key: 'kitInstances', label: 'Kit instances per company', unit: '', range: [0, 20, 1] },
  { group: 'Apps', key: 'kitBusinessShare', label: 'On Kit Business', unit: '%', range: [0, 100, 5] },
  { group: 'Apps', key: 'customerCloudShare', label: 'In the firm’s own cloud', unit: '%', range: [0, 100, 5] },
  { group: 'Apps', key: 'devRuns', label: 'Dev Console runs per instance', unit: '/mo', range: [0, 400, 5] },
  { group: 'Usage', key: 'runs', label: 'Workflow runs per company', unit: '/mo', range: [0, 1000000, 5000] },
  { group: 'Usage', key: 'conversations', label: 'Agent conversations per company', unit: '/mo', range: [0, 200000, 1000] },
  { group: 'Usage', key: 'commits', label: 'Gated commits per company', unit: '/mo', range: [0, 500000, 5000] },
  { group: 'Usage', key: 'hostedModelShare', label: 'On hosted models (not BYO key)', unit: '%', range: [0, 100, 5] },
  { group: 'Usage', key: 'frontierShare', label: 'Frontier-model share', unit: '%', range: [0, 100, 5] },
  { group: 'Commercial', key: 'discount1', label: 'Commit discount, year 1', unit: '%', range: [0, 50, 1] },
  { group: 'Commercial', key: 'discount2', label: 'Commit discount, year 2', unit: '%', range: [0, 50, 1] },
  { group: 'Commercial', key: 'discount3', label: 'Commit discount, year 3', unit: '%', range: [0, 50, 1] },
  { group: 'Commercial', key: 'pilot', label: 'Firm pilot (year 1)', unit: '$', range: [0, 100000, 1000] },
  { group: 'Commercial', key: 'firstPipeline', label: 'First pipeline per company', unit: '$', range: [0, 50000, 1000] },
  { group: 'Firm’s side', key: 'replacedSoftware', label: 'Software replaced per company', unit: '$/yr', range: [0, 2000000, 10000] },
  { group: 'Firm’s side', key: 'avoidedBuild', label: 'Avoided build per app', unit: '$', range: [0, 1000000, 5000] },
];

export const DEFAULTS = {
  portcos: 30, adoption1: 8, adoption2: 20, adoption3: 30,
  builders: 6, operators: 25,
  kitInstances: 3, kitBusinessShare: 50, customerCloudShare: 40, devRuns: 30,
  runs: 60000, conversations: 15000, commits: 30000, hostedModelShare: 70, frontierShare: 25,
  discount1: 10, discount2: 15, discount3: 25, pilot: 24000, firstPipeline: 9000,
  replacedSoftware: 120000, avoidedBuild: 60000,
};

const clamp = (v, [lo, hi]) => Math.min(hi, Math.max(lo, v));

export function normalize(partial = {}) {
  const out = { ...DEFAULTS };
  for (const s of INPUT_SPEC) {
    const v = Number(partial[s.key]);
    if (Number.isFinite(v)) out[s.key] = clamp(v, s.range);
  }
  // Adoption never exceeds the portfolio and never falls year over year.
  out.adoption1 = Math.min(out.adoption1, out.portcos);
  out.adoption2 = Math.min(Math.max(out.adoption2, out.adoption1), out.portcos);
  out.adoption3 = Math.min(Math.max(out.adoption3, out.adoption2), out.portcos);
  return out;
}

// URL ⇄ inputs. Only keys that differ from the defaults are written, so a
// plain /concepts/portfolio?fig=deal-model stays short and a NoVA step can
// say `?fig=deal-model&portcos=45&adoption3=45`.
export function parseInputs(params) {
  const get = typeof params?.get === 'function' ? (k) => params.get(k) : (k) => params?.[k];
  const partial = {};
  for (const s of INPUT_SPEC) { const v = get(s.key); if (v != null && v !== '') partial[s.key] = v; }
  return normalize(partial);
}
export function encodeInputs(inputs, params = null) {
  const next = new URLSearchParams(params || undefined);
  for (const s of INPUT_SPEC) {
    if (inputs[s.key] === DEFAULTS[s.key]) next.delete(s.key);
    else next.set(s.key, String(inputs[s.key]));
  }
  return next;
}

// ---- the model -------------------------------------------------------------------
const pct = (v) => v / 100;

export function runModel(partial = {}) {
  const x = normalize(partial);
  const R = RATE_CARD; const C = COST_CARD;
  const adoption = [x.adoption1, x.adoption2, x.adoption3];
  const discounts = [pct(x.discount1), pct(x.discount2), pct(x.discount3)];
  const years = [];
  let prevLive = 0;
  for (let y = 0; y < 3; y++) {
    const live = adoption[y];
    const newLive = Math.max(0, live - prevLive);
    prevLive = live;
    const ramp = USAGE_RAMP[y];
    const disc = discounts[y];

    // --- per portco, per month ---
    const kitBiz = x.kitInstances * pct(x.kitBusinessShare);
    const kitStd = x.kitInstances - kitBiz;
    const hostedInstances = x.kitInstances * (1 - pct(x.customerCloudShare));

    const creditsUsed = ramp * (x.runs * R.credits.run + x.conversations * R.credits.conversation + x.commits * R.credits.commit
      + Math.round(x.commits * 0.3) * R.credits.hitl); // ≈ 30% of gated commits pause for a person
    const creditOverage = Math.max(0, creditsUsed - R.enterpriseCreditsMo);

    const hostedTokens = ramp * x.conversations * pct(x.hostedModelShare) * R.tokensPerConversation; // tokens
    const tokenRevenue = Math.max(0, (hostedTokens / 1000) * R.hostedTokenUsdPer1k - R.hostedTokenAllowanceUsdMo);

    const devRuns = x.kitInstances * x.devRuns;
    const devIncluded = kitStd * R.kitDevRunsIncluded + kitBiz * R.kitBusinessDevRunsIncluded;
    const devOverage = Math.max(0, devRuns - devIncluded);

    const seatOverage = Math.max(0, x.builders - R.enterpriseBuilders) * R.extraBuilderMo
      + Math.max(0, x.operators - R.enterpriseOperators) * R.extraOperatorMo;

    const platformMo = R.enterpriseBaseMo * (1 - disc);
    const kitMo = (kitStd * R.kitMo + kitBiz * R.kitBusinessMo) * (1 - disc);

    const perMo = {
      platform: platformMo,
      kit: kitMo,
      seats: seatOverage,
      credits: creditOverage * R.creditUsd,
      tokens: tokenRevenue,
      devRuns: devOverage * R.devRunUsd,
    };
    const perPortcoMo = Object.values(perMo).reduce((a, b) => a + b, 0);

    // --- revenue, the year ---
    const m = 12 * live;
    const servicesUsd = (y === 0 ? x.pilot : 0) + newLive * x.firstPipeline;
    const revenue = [
      { key: 'platform', label: 'Platform Enterprise (per company)', usd: perMo.platform * m, basis: 'rate card' },
      { key: 'kit', label: 'App Kit licenses', usd: perMo.kit * m + R.kitEnterpriseFirmMo * 12 * (live > 0 ? 1 : 0), basis: 'rate card' },
      { key: 'seats', label: 'Seats past the bundle', usd: perMo.seats * m, basis: 'proposed' },
      { key: 'credits', label: 'Runtime Credits past the bundle', usd: perMo.credits * m, basis: 'rate card' },
      { key: 'tokens', label: 'Hosted model tokens', usd: perMo.tokens * m, basis: 'rate card' },
      { key: 'devRuns', label: 'Dev Console runs past the bundle', usd: perMo.devRuns * m, basis: 'rate card' },
      { key: 'services', label: 'Pilot + first pipelines', usd: servicesUsd, basis: 'rate card' },
    ];
    const revenueTotal = revenue.reduce((a, l) => a + l.usd, 0);

    // --- cost of revenue ---
    const hostMo = hostedInstances * ((1 - pct(x.kitBusinessShare)) * C.hostKitMo.usd + pct(x.kitBusinessShare) * C.hostKitBusinessMo.usd);
    const inferenceMo = (hostedTokens / 1000) * ((1 - pct(x.frontierShare)) * C.routedTokenPer1k.usd + pct(x.frontierShare) * C.frontierTokenPer1k.usd);
    const cogs = [
      { key: 'hosting', label: 'Managed hosting (hosted instances)', usd: hostMo * m, basis: C.hostKitMo.basis },
      { key: 'compute', label: 'Runtime compute (all credits)', usd: creditsUsed * C.creditCompute.usd * m, basis: C.creditCompute.basis },
      { key: 'inference', label: 'Model inference (hosted tokens)', usd: inferenceMo * m, basis: 'actual' },
      { key: 'devRuns', label: 'Dev Console agent runs (all runs)', usd: devRuns * C.devRun.usd * m, basis: C.devRun.basis },
      { key: 'plane', label: 'Control plane + named support', usd: (C.planePerPortcoMo.usd + C.supportPerPortcoMo.usd) * m, basis: 'estimated' },
      { key: 'services', label: 'Services delivery', usd: servicesUsd * C.servicesDelivery.share, basis: C.servicesDelivery.basis },
    ];
    const cogsTotal = cogs.reduce((a, l) => a + l.usd, 0);
    const grossMargin = revenueTotal - cogsTotal;

    // --- the firm's side ---
    const replaced = live * x.replacedSoftware;
    const avoided = newLive * x.kitInstances * x.avoidedBuild;
    const firmSavings = { replaced, avoided, spend: revenueTotal, net: replaced + avoided - revenueTotal };

    years.push({
      year: y + 1, live, newLive, ramp, discount: disc,
      perPortcoMo, perMo, creditsUsed, hostedTokens, devRuns,
      revenue, revenueTotal, cogs, cogsTotal, grossMargin, gmPct: revenueTotal > 0 ? grossMargin / revenueTotal : 0,
      recurringArr: (revenueTotal - servicesUsd), // run-rate without one-time services
      firmSavings,
    });
  }
  return {
    inputs: x,
    years,
    arr: years.map((y) => y.recurringArr),
    total3: years.reduce((a, y) => a + y.revenueTotal, 0),
    margin3: years.reduce((a, y) => a + y.grossMargin, 0),
  };
}

export const usd = (v, opts = {}) => {
  const n = Number(v) || 0;
  const abs = Math.abs(n);
  const s = abs >= 1e6 ? `$${(abs / 1e6).toFixed(opts.m ?? 2)}M` : abs >= 1e3 ? `$${Math.round(abs / 1e3)}k` : `$${Math.round(abs)}`;
  return n < 0 ? `−${s}` : s;
};
